import { event as gtagEvent } from "vue-gtag";
import type { PostHog } from "posthog-js";
import {
  createAnalytics,
  multiAdapter,
  type AnalyticsAdapter,
  type AnalyticsProps,
} from "@donoor/observability";

/**
 * Composable analytics centralisé donoor.org.
 * Combine Google Analytics (vue-gtag) + PostHog (plugin posthog.client.ts).
 *
 * Amplitude a été retiré (2026-07 optimisation perf/LCP) : il faisait doublon
 * avec PostHog (product analytics) et son package `@amplitude/unified`
 * embarquait un moteur de session-replay (rrweb ~90-140 KB) qui polluait
 * l'entry JS de toutes les pages, dégradant le LCP mobile des landing Ads.
 * PostHog (déjà lazy + opt-in) couvre le product analytics + funnel + replay.
 *
 * Cf. ADR-009 Z0.9 — Architecture monorepo 4 couches.
 */

// Adapter Google Analytics via vue-gtag (track event uniquement —
// pas d'identify/reset gtag dans la convention legacy).
const gtagAdapter: AnalyticsAdapter = {
  identify() {
    // gtag user_properties peuvent être set ici si besoin futur.
  },
  reset() {
    // gtag.send('config', GA_ID, { user_id: null }) pas implémenté pour l'instant.
  },
  track(name, props) {
    gtagEvent(name, {
      event_category: (props?.event_category as string) ?? "engagement",
      event_label: (props?.event_label as string) ?? undefined,
      ...props,
    });
  },
};

// Adapter PostHog (product analytics + session replay).
// ⚠️ On NE fait PAS `import posthog from "posthog-js"` (import statique) :
// ce composable est appelé au boot par PageHero (above-the-fold), donc un
// import statique tirait TOUT le SDK posthog-js + rrweb (~250-400 KB, ES5)
// dans l'entry chunk de CHAQUE page, annulant le lazy-load du plugin
// posthog.client.ts et plombant le LCP mobile (audit perf 2026-07-25).
// À la place on lit la façade `$posthog` fournie par le plugin via
// useNuxtApp() AU MOMENT de l'appel : elle est no-op tant que le SDK n'est
// pas init (staging/dev, hors prod, avant l'idle, ou opt-out RGPD), et le
// vrai SDK n'est chargé QUE par l'import dynamique du plugin.
const getPosthog = (): PostHog | null => {
  // useNuxtApp() n'est dispo qu'au runtime client dans un contexte Nuxt.
  const nuxtApp = useNuxtApp();
  return (nuxtApp.$posthog as PostHog | null) ?? null;
};
const posthogAdapter: AnalyticsAdapter = {
  identify(userId, traits) {
    const posthog = getPosthog();
    if (!posthog?.__loaded) return;
    posthog.identify(
      userId,
      traits as Record<string, string | number | boolean> | undefined,
    );
  },
  reset() {
    const posthog = getPosthog();
    if (!posthog?.__loaded) return;
    posthog.reset();
  },
  track(name, props) {
    const posthog = getPosthog();
    if (!posthog?.__loaded) return;
    posthog.capture(name, props ?? undefined);
  },
};

const analytics = createAnalytics(
  multiAdapter(gtagAdapter, posthogAdapter),
);

export const useAnalytics = () => {
  const trackEvent = (name: string, props?: AnalyticsProps) =>
    analytics.track(name, props);

  const trackSignupClick = (source?: string) =>
    analytics.track("signup_click", {
      source: source || "unknown",
      event_category: "engagement",
      event_label: source || "unknown",
    });

  const trackDonationStarted = (props: {
    campaign_id?: string | number;
    organization_id?: string | number;
    source?: string;
  }) => analytics.track("donation_started", props);

  const trackDonationAmountSelected = (props: {
    amount: number;
    currency?: string;
    is_custom?: boolean;
    campaign_id?: string | number;
  }) => analytics.track("donation_amount_selected", props);

  const trackDonationCompleted = (props: {
    amount: number;
    currency?: string;
    transaction_id?: string;
    payment_provider?: "stripe" | "paypal";
    campaign_id?: string | number;
    organization_id?: string | number;
  }) => analytics.track("donation_completed", props);

  const trackDonationFailed = (props: {
    reason?: string;
    payment_provider?: "stripe" | "paypal";
    amount?: number;
  }) => analytics.track("donation_failed", props);

  const trackPageView = (page: string, props?: AnalyticsProps) =>
    analytics.track("page_view", { page, ...props });

  return {
    trackSignupClick,
    trackDonationStarted,
    trackDonationAmountSelected,
    trackDonationCompleted,
    trackDonationFailed,
    trackPageView,
    trackEvent,
    identify: analytics.identify,
    reset: analytics.reset,
  };
};
